import { AddItemSlotsRequest } from "../../../apis/requests/AddItemSlotsRequest";
import { AbilitiesByLevelCollection } from "../../abilities/AbilitiesByLevelCollection";
import { CharacterAbility } from "../../abilities/Ability";
import { CharacterHelpers, LocalCharacterInitializer } from "../../characters/Character";
import { ModSlot } from "../items/mods/ModSlot";
import { OwnedItemReference, OwnedSkillReference } from "../../Ownership";
import { RecordStatus } from "../../RecordStatus";
import { Roller } from "../Roller";
import { SkillName } from "./SkillMap";
import { ActiveAbility } from "../../abilities/ActiveAbility";
import { ReactiveAbility } from "../../abilities/ReactiveAbility";
import { PassiveAbility } from "../../abilities/PassiveAbility";
import { ItemInitializer, WeaponInitializer } from "../items/ItemInitializers";
import { getUniqueIdentifier } from "../../../utils/GUID";

export type SkillInitializer = {
    id: string;
    name: SkillName;
    description: string;
    featuresByLevel: AbilitiesByLevelCollection[];
    isSlotChangingSkill: boolean;
    level4SlotChangeId: string;
    level8SlotChangeId: string;
    changeSlotTriggerTags: string[];
    customSlotTags: string[];
    recordStatus: RecordStatus;
}

const IdToNameMap: {[name in SkillName]: string} = Object.freeze({
    'Athletics': 'a6984189-0dfb-49d8-b779-737ff9d19605',
    'Chemistry': '1f190e38-0776-4f98-914f-0a63cfa4f11b',
    'Command': '045e0863-e6f7-43af-94bb-00bc7c58f9af',
    'Electronics': '02fd9fc3-004a-46b3-92a6-9d72217de5c9',
    'Explosives': 'e8e423c8-b76b-4232-beaf-21cd8771aa81',
    'Firearms': 'c0e6c776-f5d8-454e-9130-c4fd2c09dfce',
    'Machinery': '12e0186b-14d7-4c95-be0a-40b72699011f',
    'Medicine': 'b0406ca3-3d1d-41e1-b262-fabd2585b711',
    'Melee': 'f19bda1e-875f-4575-96b8-2767d59ca60f',
    'Subterfuge': '8f891570-ffc4-43bd-8feb-c6356ec23598',
});

const getAdjustedLevel = (skill: OwnedSkillReference) => {
    if (skill.override && skill.override > 0) {
        return skill.override;
    }
    return skill.level + skill.adjustment;
}

const getSkillPointsUsed = (reference: OwnedSkillReference) => reference.level - 1;

const getEmpowermentBonus = (reference: OwnedSkillReference) => Math.floor(reference.level / 2);

const getRoller = (reference: OwnedSkillReference): Roller => new Roller(getAdjustedLevel(reference));

const getAcquiredFeatures = (skill: SkillInitializer, reference: OwnedSkillReference) => {
    return skill.featuresByLevel.filter(fbl => fbl.level <= reference.level);
}

const getActions = (allSkills: SkillInitializer[], reference: OwnedSkillReference): ActiveAbility[] => {
    const skill = allSkills.find(s => s.id == reference.id);
    if (skill == null) {
        return [];
    }
    return getAcquiredFeatures(skill, reference).mapMany(fbl => fbl.activeAbilities);
}

const getReactions = (allSkills: SkillInitializer[], reference: OwnedSkillReference): ReactiveAbility[] => {
    const skill = allSkills.find(s => s.id == reference.id);
    if (skill == null) {
        return [];
    }
    return getAcquiredFeatures(skill, reference).mapMany(fbl => fbl.reactiveAbilities);
}

const getPassives = (allSkills: SkillInitializer[], reference: OwnedSkillReference): PassiveAbility[] => {
    const skill = allSkills.find(s => s.id == reference.id);
    if (skill == null) {
        return [];
    }
    return getAcquiredFeatures(skill, reference).mapMany(fbl => fbl.passiveAbilities);
}

const getFeatures = (allSkills: SkillInitializer[], reference: OwnedSkillReference): CharacterAbility[] => {
    const skill = allSkills.find(s => s.id == reference.id);
    if (skill == null) {
        console.error(`Attempted to get features from nonexistent skill ${reference.id}.`);
        return [];
    }
    return getAcquiredFeatures(skill, reference)
        .mapMany(fbl => [...fbl.activeAbilities, ...fbl.passiveAbilities, ...fbl.reactiveAbilities]
            .map(a => ({ ...a, source: `${skill.name} ${fbl.level}` })));
}

const getAddSlotChanges = (skill: SkillInitializer, allItems: ItemInitializer[], references: OwnedItemReference[], addedBy: string): AddItemSlotsRequest[] => {
    const changes: AddItemSlotsRequest[] = [];
    for (let itemReference of references) {
        const item = allItems.find(i => i.id == itemReference.id);
        if (item == null) {
            continue;
        }

        const slots = []
        const existingSlotCount = itemReference.customSlots.filter(cs => cs.addedBy == addedBy).length;
        for (let handIndex = existingSlotCount; handIndex < item.handsUsedModifier; handIndex++) {
            slots.push({ id: getUniqueIdentifier(), addedBy, slotTypes: skill.customSlotTags });
        }
        changes.push({ itemInstanceId: itemReference.instanceId, slots });
    }
    return changes;
}

const getRemoveSlotChanges = (references: OwnedItemReference[], addedBy: string): ModSlot[] => {
    return references.mapMany(reference => reference.customSlots).filter(slot => slot.addedBy == addedBy);
}

const getSlotChangesOnLoadForItem = (skill: SkillInitializer, reference: OwnedSkillReference, allItems: ItemInitializer[], itemReference: OwnedItemReference): SlotChangeRequest[] => {
    const changes: SlotChangeRequest[] = [];
    const weapon = allItems.find(i => i.id == itemReference.id) as WeaponInitializer;
    if (weapon?.type != 'Weapon') {
        return changes;
    }

    const slotsAt4 = itemReference.customSlots.filter(cs => cs.addedBy == skill.level4SlotChangeId);
    if (reference.level >= 4 && slotsAt4.length < weapon.hands) {
        const add: SlotChangeRequest = { type: 'ADD', changes: getAddSlotChanges(skill, allItems, [itemReference], skill.level4SlotChangeId) };
        changes.push(add);
    }
    else {
        const remove: SlotChangeRequest = { type: 'REMOVE', changes: getRemoveSlotChanges([itemReference], skill.level4SlotChangeId) };
        changes.push(remove);
    }

    const slotsAt8 = itemReference.customSlots.filter(cs => cs.addedBy == skill.level8SlotChangeId);
    if (reference.level >= 8 && slotsAt8.length < weapon.hands) {
        const add: SlotChangeRequest = { type: 'ADD', changes: getAddSlotChanges(skill, allItems, [itemReference], skill.level8SlotChangeId) };
        changes.push(add);
    }
    else {
        const remove: SlotChangeRequest = { type: 'REMOVE', changes: getRemoveSlotChanges([itemReference], skill.level8SlotChangeId) };
        changes.push(remove);
    }

    return changes;
}

const getItemsToAffectForSlotChange = (skill: SkillInitializer, character: LocalCharacterInitializer, allItems: ItemInitializer[]) => {
    return CharacterHelpers.getInventory(character)
        .filter(weaponReference => {
            const item = allItems.find(i => i.id == weaponReference.id);
            if (item == null) {
                return false;
            }
            return item.type == 'Weapon'
                && item.tags.intersection(skill.changeSlotTriggerTags).length > 0
                && character.pendingProperties[weaponReference.instanceId];
        });
}

const getSlotChangesOnLoad = (skill: SkillInitializer, character: LocalCharacterInitializer, allItems: ItemInitializer[]): SlotChangeRequest[] => {
    const reference = character.skills.find(s => s.id == skill.id);
    if (reference == null) {
        console.error(`Attempted to get slot changes from nonexistent skill ${skill.id}.`);
        return [];
    }
    const weaponsToAffect = getItemsToAffectForSlotChange(skill, character, allItems);
    let changes: SlotChangeRequest[] = [];
    for (let weaponReference of weaponsToAffect) {
        changes = [...changes, ...getSlotChangesOnLoadForItem(skill, reference, allItems, weaponReference)]
    }

    const addChanges = changes.filter<AddSlotChangeRequest>(IsAddSlotChangeRequest).mapMany(request => request.changes);
    const removeChanges = changes.filter<RemoveSlotChangeRequest>(IsRemoveSlotChangeRequest).mapMany(request => request.changes);

    changes = [];
    if (addChanges.any()) {
        changes.push({ type: 'ADD', changes: addChanges });
    }
    if (removeChanges.any()) {
        changes.push({ type: 'REMOVE', changes: removeChanges });
    }
    return changes;
}


export const SkillHelpers = Object.freeze({
    getAdjustedLevel,
    getSkillPointsUsed,
    getEmpowermentBonus,
    getRoller,
    getActions,
    getReactions,
    getPassives,
    getFeatures,
    getSlotChangesOnLoad,

    MinValue: 1,
    MaxValue: 10,
    MaxSkillDifference: 2,
    PointsPerLevel: 3,
    IdToNameMap,
});

export const DefaultSkillInitializer: SkillInitializer = {
    id: '',
    name: 'Athletics',
    description: '',
    isSlotChangingSkill: false,
    level4SlotChangeId: '',
    level8SlotChangeId: '',
    changeSlotTriggerTags: [],
    customSlotTags: [],
    featuresByLevel: [],
    recordStatus: RecordStatus.Published
}

export type AddSlotChangeRequest = { type: 'ADD', changes: AddItemSlotsRequest[] }
export type RemoveSlotChangeRequest = { type: 'REMOVE', changes: ModSlot[] };
export type NoSlotChangeRequest = { type: 'NONE' }

export function IsAddSlotChangeRequest(request: SlotChangeRequest): request is AddSlotChangeRequest {
    return request.type == 'ADD';
}

export function IsRemoveSlotChangeRequest(request: SlotChangeRequest): request is RemoveSlotChangeRequest {
    return request.type == 'REMOVE';
}

export function IsNoSlotChangeRequest(request: SlotChangeRequest): request is NoSlotChangeRequest {
    return request.type == 'NONE';
}

export type SlotChangeRequest = AddSlotChangeRequest | RemoveSlotChangeRequest | NoSlotChangeRequest;